import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { invitationsApi, tokenStore, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { brand } from "@/theme/themeTokens";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await invitationsApi.accept(token, password, name || undefined);
      tokenStore.set(data.token);
      setUser(data.user);
      navigate("/today", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, "This invitation is invalid or has expired."));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="flex flex-1 flex-col justify-center px-5 py-10">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex flex-col items-start">
            <Logo />
            <h1 className="mt-6 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Accept your invitation</h1>
            <p className="mt-1 text-sm text-slate-500">Set a password to join your team on {brand.name}.</p>
          </div>

          {!token ? (
            <div data-testid="accept-no-token" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700">
              This link is missing its invitation token. Ask your admin to reissue it.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="accept-invitation-form">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Your name</label>
                <input data-testid="accept-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  className="min-h-[52px] w-full rounded-lg border border-slate-200 px-3 text-sm" placeholder="Jane Rep" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Password</label>
                <input data-testid="accept-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="min-h-[52px] w-full rounded-lg border border-slate-200 px-3 text-sm" placeholder="At least 8 characters" />
              </div>
              {error && <div data-testid="accept-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700">{error}</div>}
              <button type="submit" disabled={loading || password.length < 8} data-testid="accept-submit"
                className="min-h-[52px] w-full rounded-lg bg-teal-700 font-semibold text-white shadow-sm hover:bg-teal-800 disabled:opacity-60">
                {loading ? "Joining…" : "Join team"}
              </button>
            </form>
          )}
        </div>
      </div>
      <footer className="pb-safe px-5 py-4 text-center text-xs text-slate-400">{brand.name} {brand.version} · {brand.phase}</footer>
    </div>
  );
}
